/**
 * @author       luis.orihuela@zumper.com (Luis Orihuela)
 * @copyright    Copyright (c) 2024, Zumper
 * @description  getTourAvailability thunk
 */

import { type PropertyTourIntegration, TourAvailability, TourIntegration } from '@zcode/bizos'
import { IntegratedTourType, TourProvider } from '@zcode/enums'
import { BetaFeature, isBetaFeatureEnabled, timeoutAsync } from '@zcode/nr-utils'
import * as Api from '@zcode/nr/es/shared/api'
import { getRouteProps } from '@zcode/nr/es/shared/packages/history-middleware/actions'
import type { GetState, Property } from '@zcode/state-type'
import { isSameDay } from 'date-fns'
import { logger } from '../../../logger'
import type { Dispatch } from '../../types'
import {
  errorFetchingTourAvailability,
  errorFetchingTourIntegrationForGroupId,
  receiveSelectedTourType,
  receiveTourAvailability,
  receiveTourIntegration,
  startFetchingTourAvailability,
  startFetchingTourIntegrationForGroupId,
} from '../actions'
import {
  selectFetchingTourAvailability,
  selectFetchingTourIntegrationForGroupId,
  selectSelectedTourType,
  selectTourAvailability,
  selectTourAvailabilityFetchedAt,
  selectTourIntegrationData,
  selectTourIntegrationForGroupIdFetchedAt,
} from '../selectors'

const fetchTourIntegrationPromises = new Map<number, Promise<void>>()

export const fetchTourIntegration =
  ({
    groupId,
    isTourDisabled,
    mightHaveInstantTourScheduling,
  }: Pick<Property, 'groupId' | 'isTourDisabled' | 'mightHaveInstantTourScheduling'>) =>
  async (dispatch: Dispatch, getState: GetState) => {
    const state = getState()
    const fetchingForGroupId = selectFetchingTourIntegrationForGroupId(state, groupId)
    const fetchedAt = selectTourIntegrationForGroupIdFetchedAt(state, groupId)
    if (!fetchedAt || !isSameDay(new Date(), fetchedAt.getTime())) {
      // The `groupId > 0` check is because we haven't yet expanded the integrated tours to listings,
      // so we don't bother making an API call that we know will have no results
      if (isTourDisabled || !mightHaveInstantTourScheduling || groupId > 0) {
        dispatch(receiveTourIntegration(groupId))
        return
      }
      let fetchTourIntegrationPromise = fetchTourIntegrationPromises.get(groupId)
      if (!fetchingForGroupId || !fetchTourIntegrationPromise) {
        fetchTourIntegrationPromise = _fetchTourIntegration(dispatch, getState, groupId)
        fetchTourIntegrationPromises.set(groupId, fetchTourIntegrationPromise)
      }
      return fetchTourIntegrationPromise
    }
  }

async function _fetchTourIntegration(dispatch: Dispatch, getState: GetState, groupId: number) {
  try {
    dispatch(startFetchingTourIntegrationForGroupId(groupId))
    const response = await _maybeMock(dispatch, Api.tourIntegration.get)({ group_id: groupId })
    if (response.integration && !response.availability.availableTimes.length) {
      let { availability }: { availability: TourAvailability | undefined } = response
      for (const type of response.integration.tourTypes) {
        if (type === response.availability.type) {
          continue
        }
        availability = await dispatch(
          fetchTourAvailability({ integration: response.integration, type })
        )
        if (availability?.availableTimes.length) {
          break
        }
      }
    }
    dispatch(receiveTourIntegration(response.availability ? response : groupId))
    if (response.integration) {
      const state = getState()
      const selectedTourType = selectSelectedTourType(state)
      const tourAvailability = selectTourAvailability(state, groupId)
      const typesWithLoadedAvailability: IntegratedTourType[] = []
      if (tourAvailability) {
        for (const [type, availability] of tourAvailability.entries()) {
          if (availability.availableTimes?.length) {
            typesWithLoadedAvailability.push(type)
          }
        }
      }
      if (
        !typesWithLoadedAvailability.includes(selectedTourType) &&
        typesWithLoadedAvailability.length
      ) {
        dispatch(receiveSelectedTourType(typesWithLoadedAvailability[0]))
      }
    }
  } catch (err) {
    logger.error('failed to fetch tour integration for groupId', groupId, err)
    dispatch(errorFetchingTourIntegrationForGroupId(groupId))
  } finally {
    fetchTourIntegrationPromises.delete(groupId)
  }
}

const fetchTourAvailabilityResults = new Map<
  `${number},${IntegratedTourType}`,
  Promise<TourAvailability | undefined>
>()

export const fetchTourAvailability =
  ({
    entity,
    integration: originalIntegration,
    type,
  }:
    | {
        entity: Pick<Property, 'groupId' | 'isTourDisabled' | 'mightHaveInstantTourScheduling'>
        integration?: never
        type: IntegratedTourType
      }
    | {
        entity?: never
        integration: PropertyTourIntegration
        type: IntegratedTourType
      }) =>
  async (dispatch: Dispatch, getState: GetState) => {
    const groupId = entity ? entity.groupId : originalIntegration.groupId
    if (!groupId) {
      return
    }
    let integration = originalIntegration
    if (!originalIntegration) {
      await dispatch(fetchTourIntegration(entity))
      integration = selectTourIntegrationData(getState(), entity.groupId) ?? undefined
    }
    if (!integration || integration.groupId !== groupId || !integration.tourTypes.includes(type)) {
      return
    }
    const state = getState()
    const fetchedAt = selectTourAvailabilityFetchedAt(state, groupId, type)
    if (fetchedAt && Math.abs(Date.now() - fetchedAt.getTime()) < 5000) {
      // it's possible that we just did `fetchTourIntegration(...)` a few lines above here and that that
      // response included the tour availability for the type we're interested in.
      // if that happened, we don't need to bother fetching it again
      return
    }
    const key = `${integration.groupId},${type}` as const
    const isFetching = selectFetchingTourAvailability(state, groupId, type)
    const result = fetchTourAvailabilityResults.get(key)

    if (!isFetching || !result) {
      const result = _fetchTourAvailability(dispatch, groupId, type)
      result.finally(() => {
        fetchTourAvailabilityResults.delete(key)
      })
      fetchTourAvailabilityResults.set(key, result)
      return result
    }
    return result
  }

async function _fetchTourAvailability(
  dispatch: Dispatch,
  groupId: number,
  type: IntegratedTourType
) {
  try {
    dispatch(startFetchingTourAvailability({ groupId, type }))
    const availability = await _maybeMock(
      dispatch,
      Api.tourAvailability.get
    )({
      group_id: groupId,
      tour_type: type,
    })
    dispatch(receiveTourAvailability({ availability, groupId }))
    return availability
  } catch (err) {
    logger.warn('failed to receive available times from knock: ', err)
    dispatch(errorFetchingTourAvailability({ groupId, type }))
  }
}

function _maybeMock<
  Fn extends (typeof Api)['tourAvailability']['get'] | (typeof Api)['tourIntegration']['get'],
>(
  dispatch: Dispatch,
  fn: Fn
): Fn | ((typeof Api)['tourAvailability']['get'] & (typeof Api)['tourIntegration']['get']) {
  const { location } = dispatch(getRouteProps())
  if (isBetaFeatureEnabled(location, BetaFeature.MOCK_TOUR_AVAILABILITY)) {
    async function mock(
      params: Parameters<(typeof Api)['tourAvailability']['get']>[0]
    ): ReturnType<(typeof Api)['tourAvailability']['get']>
    async function mock(
      params: Parameters<(typeof Api)['tourIntegration']['get']>[0] & { tour_type?: never }
    ): ReturnType<(typeof Api)['tourIntegration']['get']>
    async function mock(
      params:
        | Parameters<(typeof Api)['tourAvailability']['get']>[0]
        | (Parameters<(typeof Api)['tourIntegration']['get']>[0] & { tour_type?: never })
    ): Promise<
      | Awaited<ReturnType<(typeof Api)['tourAvailability']['get']>>
      | Awaited<ReturnType<(typeof Api)['tourIntegration']['get']>>
    >
    async function mock(
      params:
        | Parameters<(typeof Api)['tourAvailability']['get']>[0]
        | (Parameters<(typeof Api)['tourIntegration']['get']>[0] & { tour_type?: never })
    ): Promise<
      | Awaited<ReturnType<(typeof Api)['tourAvailability']['get']>>
      | Awaited<ReturnType<(typeof Api)['tourIntegration']['get']>>
    > {
      await timeoutAsync(5000)
      const today = new Date().toISOString().substring(0, 10)
      const tomorrow = new Date(Date.now() + 24 * 3600000).toISOString().substring(0, 10)
      const day4 = new Date(Date.now() + 3 * 24 * 3600000).toISOString().substring(0, 10)
      let available_times = [
        `${today}T12:00:00-04:00`,
        `${today}T12:30:00-04:00`,
        `${today}T13:00:00-04:00`,
        `${tomorrow}T09:00:00-04:00`,
        `${tomorrow}T10:00:00-04:00`,
        `${tomorrow}T11:00:00-04:00`,
      ]
      if (params.tour_type) {
        if (params.tour_type !== IntegratedTourType.SELF_GUIDED) {
          available_times = [`${day4}T11:00:00-04:00`, `${day4}T15:00:00-04:00`]
        }
        return new TourAvailability({
          available_times,
          type: params.tour_type,
        })
      }
      return new TourIntegration({
        availability: {
          available_times,
          type: IntegratedTourType.SELF_GUIDED,
        },
        integration: {
          created_on: 1234567890,
          group_id: params.group_id,
          modified_on: 1234567890,
          property_tour_integration_id: 2,
          provider: TourProvider.HYLY,
          provider_enabled: true,
          provider_id: 'invalid',
          tour_types: [IntegratedTourType.SELF_GUIDED, IntegratedTourType.LIVE_VIRTUAL],
          zumper_enabled: true,
        },
      })
    }
    return mock
  }
  return fn
}
